import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { isEmptyObject, isStringEmpty, parseEmptyArray } from '@common/helpers';
import { JuniorPilot, PassengerJuniorPilotMap } from '@common/interfaces/mybooking/order-services.interface';
import { MyBookingServiceService } from '@common/services/my-booking/my-booking-service/my-booking-service.service';
import { downloadPDF, showPdfInNewWindow } from '@common/services/next-action/next-action.helpers';
import { SupportComponent } from '@lib/components/support-class';
import { ErrorType } from '@lib/interfaces/errors';
import { AswServiceStoreService, CommonLibService, PageLoadingService } from '@lib/services';
import { Subscription, take } from 'rxjs';
import {
  Bound,
  GetOrderResponseData,
  JuniorPilotGetReceiptRequest,
  DefaultService as ServicingApiService,
  Traveler,
  Type1,
  Type1Arrival,
  Type1Departure,
} from 'src/sdk-servicing';

@Component({
  selector: 'asw-junior-pilot',
  templateUrl: './junior-pilot.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JuniorPilotComponent extends SupportComponent {
  private _subscriptions: Subscription = new Subscription();
  public isScrollJuniorPilot: boolean = true;

  /** 注文応答データの取得 */
  pnr!: GetOrderResponseData;

  _subscription: Subscription = new Subscription();

  juniorPilotFlightInforList!: Array<Type1>;

  juniorPilotPassengerInforList!: Array<Traveler>;

  flights: Array<Type1> = [];
  travelers: Array<Traveler> = [];

  /** [10.2] ジュニアパイロット申込書控えダウンロード */
  isDisplayJuniorPilotPdf: boolean = false;

  downloadPDF;

  getJuniorPilotMock = () => {
    return this._http.get('assets/mock-data/junior-pilot/get-receipt.json');
  };

  pdfWindow: WindowProxy | null = null;

  constructor(
    private _http: HttpClient,
    public change: ChangeDetectorRef,
    private _common: CommonLibService,
    private _myBookingServiceService: MyBookingServiceService,
    private readonly _servicingApiService: ServicingApiService,
    private _aswServiceStoreService: AswServiceStoreService,
    private readonly _loadingService: PageLoadingService
  ) {
    super(_common);
    this.downloadPDF = downloadPDF;
  }

  init(): void {
    this._subscriptions.add(
      this._myBookingServiceService.mybookingServiceState$.subscribe((state) => {
        this.pnr = state?.pnr ?? ({} as GetOrderResponseData);
        /** 注文ストアサービスを取得する */
        this.juniorPilotFlightInforList = state?.juniorPilotFlightInforList ?? [];
        this.juniorPilotPassengerInforList = state?.juniorPilotPassengerInforList ?? [];
        this.travelers = parseEmptyArray(state?.pnr?.travelers);
        this.flights = parseEmptyArray(this.pnr?.air?.bounds?.flatMap((bound: Bound) => bound.flights ?? []));
        this.handleShowBtnJuniorPilotPdf();
        this.change.markForCheck();
      })
    );
  }

  /** Check if table slider is scrolling or not with Junior Pilot */
  scrollJuniorPilot(value: boolean) {
    this.isScrollJuniorPilot = value;
  }

  /**
   * create PNR情報取得APIリクエストデータ型
   * @param isMask マスキング要否。<br/> 指定なしの場合true：マスキング要とする
   * @returns PNR情報取得APIリクエストデータ型
   */
  createOrdersJuniorPilotGetReceiptApiRequestParam(): JuniorPilotGetReceiptRequest {
    return {
      orderId: this._aswServiceStoreService.aswServiceData.orderId ?? '',
      credential: {
        firstName: this._aswServiceStoreService.aswServiceData.firstName ?? '',
        lastName: this._aswServiceStoreService.aswServiceData.lastName ?? '',
      },
    };
  }
  /** (26)「ジュニアパイロット申込書控えダウンロード」ボタン押下処理 */
  clickViewPdf(): void {
    this.pdfWindow = window.open('');
    this._loadingService.startLoading();
    /** 1.2. 共通部品「PDF生成サービス接続」(G03-511)より受け取った、base64形式の文字列 */
    const getReceiptRequestParam = this.createOrdersJuniorPilotGetReceiptApiRequestParam();
    this._servicingApiService
      .ordersJuniorPilotGetReceiptPost(getReceiptRequestParam)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          const category = 'reportForm';
          const propertyName = 'downloadFileName.servicing.juniorPilotReceipt';
          const pdfName = this._common.aswMasterService.getMPropertyByKey(category, propertyName);
          showPdfInNewWindow(response.data, pdfName, this.pdfWindow);
        },
        error: (error) => {
          this.pdfWindow?.close();
          this._common.errorsHandlerService.setNotRetryableError({
            errorType: ErrorType.BUSINESS_LOGIC,
            errorMsgId: 'E1370',
            apiErrorCode: error.errorCode,
          });
        },
        complete: () => {
          this._loadingService.endLoading();
        },
      });
  }

  // [10.2] ジュニアパイロット申込書控えダウンロード
  handleShowBtnJuniorPilotPdf(): void {
    /**
     * 1.PNR情報取得API応答.data.services.juniorPilotが存在するかつ、　PNR情報取得API応答.data.services.juniorPilot.＜全セグメント情報＞.<全搭乗者情報>.serviceStatus="requested"(申込済)の場合、表示する。
     * 2.上記以外の場合、非表示する。
     */
    for (const flight of this.flights) {
      for (const traveler of this.travelers) {
        const juniorPilot = this.pnr?.services?.juniorPilot as JuniorPilot;
        const passengerJuniorPilotMap = juniorPilot?.[String(flight?.id)]?.[String(traveler?.id)];
        if (
          !isEmptyObject(juniorPilot) &&
          passengerJuniorPilotMap?.serviceStatus === PassengerJuniorPilotMap.ServiceStatusEnum.Requested
        ) {
          this.isDisplayJuniorPilotPdf = true;
          break;
        }
      }
    }
  }

  // {10.3] ジュニアパイロット申込
  isDisplayJuniorPilotApp(): boolean {
    return Boolean(this.pnr?.orderEligibilities?.juniorPilot?.isEligibleToForward);
  }

  // [10.5] 出発地
  getlocationNameDeparture(departure?: Type1Departure): string {
    const locationName = this._myBookingServiceService.onHandleCommonNo4(departure?.locationCode);
    return isStringEmpty(locationName) !== true ? locationName : departure?.locationName ?? '';
  }

  // [10.6] 到着地
  getlocationNameArrival(arrival?: Type1Arrival): string {
    const locationName = this._myBookingServiceService.onHandleCommonNo4(arrival?.locationCode);
    return isStringEmpty(locationName) !== true ? locationName : arrival?.locationName ?? '';
  }

  // [10.8] 搭乗者姓名
  getTravelerName(traveler?: Traveler): string {
    return this._myBookingServiceService.getTravelerName(traveler?.names);
  }

  // (27)「ジュニアパイロット申込」ボタン押下処理
  clickJuniorPilotApp() {
    // (27.1) 「当画面共通処理No18.未申込時ジュニアパイロット申込モーダル表示処理」を行う。
    this._myBookingServiceService.onHandleCommonNo18();
  }

  reload(): void {}

  destroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
  }
}
